import InCallIcon from '@mui/icons-material/PhoneInTalk';
import ContactTypeIcon from 'flow/components/contact/ContactTypeIcon';
import ElapsedTime from 'flow/components/ui-components/ElapsedTime';
import IconBadge from 'flow/components/ui-components/IconBadge';
import OmnichannelIcon from 'flow/components/ui-components/OmnichannelIcon';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { useAppSelector } from 'flow/hooks/useRedux';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { selectActiveQueueItem } from 'flow/selectors/queue-selectors';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { extractAbbreviatedName } from 'flow/utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import { getQueueCallDisplayText } from 'flow/utils/pbx-utils';

interface Props {
    callKey: CallRecordKey;
    queueKey?: QueueKey;
    isOmniErrand: boolean;
}

const PbxActiveCallItem = ({ callKey, queueKey, isOmniErrand }: Props) => {
    const { i18next } = useAppContext();
    const { ownContact } = useContact();

    const queueItem = useAppSelector((state) =>
        selectActiveQueueItem(state, queueKey, callKey),
    );

    const answeringAgentContact = useAppSelector((state) => {
        const agentKey = isOmniErrand
            ? queueItem.get('answeringAgentKey')
            : state.top.getIn(['uniqueIdToEntityKey', callKey]);

        return agentKey
            ? state.extensions.getIn(['extensions', agentKey, 'contact'])
            : null;
    });

    const callerNumber = isOmniErrand
        ? queueItem.get('source')
        : queueItem.getIn(['caller', 'number']) ||
          queueItem.get('OriginalCallerID') ||
          queueItem.get('Source');

    const contact = useAppSelector((state) => {
        const getContactDataForNumber = makeGetContactDataForNumber();
        //@ts-ignore
        return getContactDataForNumber(state, callerNumber);
    });

    const callerContact = queueItem.get('callerContact') || contact;
    const omniChannelType = queueItem.get('omniChannelType');
    const answeredTime =
        queueItem.get('answeredTime') || queueItem.get('answerTime');
    const callerNameOrNumber = getQueueCallDisplayText(
        callerNumber,
        callerContact,
        getCountryCode(ownContact, null),
        i18next,
    );

    return (
        <div className="hover:darken-10 flex w-full items-center justify-center gap-x-2 rounded-xs p-1">
            <div className="flex size-9 items-center justify-center rounded-md bg-gray-200 p-1 text-gray-600">
                {omniChannelType ? (
                    <OmnichannelIcon channelType={omniChannelType} />
                ) : (
                    <InCallIcon />
                )}
            </div>

            <div className="flex flex-1 flex-col">
                <div className="text-base/5 text-gray-800">
                    {callerNameOrNumber}
                </div>
                <div className="text-sm/4 text-gray-500">
                    {queueItem.get('Description')}
                </div>
            </div>

            <div className="flex flex-col text-end text-xs">
                <div className="flex gap-x-1 text-gray-900">
                    <ContactTypeIcon contact={callerContact} />
                    <IconBadge iconKey="headset" />
                    <ElapsedTime since={answeredTime} />
                </div>
                <div className="text-gray-500">
                    {extractAbbreviatedName(
                        answeringAgentContact,
                        i18next.t('status.unknown'),
                    )}
                </div>
            </div>
        </div>
    );
};

export default PbxActiveCallItem;
